import React, { useState, useEffect, useMemo } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    List,
    ListItem,
    ListItemText,
    CircularProgress
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../utils/colorUtils";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import MainCard from "../../components/cards/MainCard";
import {
    useLazyGetLocationsByTenancyIdQuery,
    useLazyGetDepartmentsByTenancyIdQuery,
    useLazyGetCitiesByTenancyIdQuery,
    useLazyGetCountriesByTenancyIdQuery,
    useLazyGetCompanyNamesByTenancyIdQuery,
    useLazyGetStatesByTenancyIdQuery,
} from "../../store/services/tenancy.service";

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 600,
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
};

EditAssignmentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    assignment: PropTypes.object,
    contentPacks: PropTypes.array,
    groups: PropTypes.array,
    onConfirm: PropTypes.func.isRequired,
    isLoading: PropTypes.bool
};

export default function EditAssignmentModal({
    open,
    onClose,
    assignment,
    contentPacks = [],
    groups = [],
    onConfirm,
    isLoading = false
}) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const { tenancyId } = useSelector((state) => state.tenancy);

    const [starterPack, setStarterPack] = useState(true);
    const [annualRefresh, setAnnualRefresh] = useState(false);
    const [selectedContentPack, setSelectedContentPack] = useState('');
    const [recipientName, setRecipientName] = useState('');

    const [triggerLocations, { data: locationsData }] = useLazyGetLocationsByTenancyIdQuery();
    const [triggerDepartments, { data: departmentsData }] = useLazyGetDepartmentsByTenancyIdQuery();
    const [triggerCities, { data: citiesData }] = useLazyGetCitiesByTenancyIdQuery();
    const [triggerCountries, { data: countriesData }] = useLazyGetCountriesByTenancyIdQuery();
    const [triggerCompanyNames, { data: companyNamesData }] = useLazyGetCompanyNamesByTenancyIdQuery();
    const [triggerStates, { data: statesData }] = useLazyGetStatesByTenancyIdQuery();

    // Populate state from assignment
    useEffect(() => {
        if (assignment) {
            setStarterPack(assignment.starterPack ?? true);
            setAnnualRefresh(assignment.refreshEnabled ?? assignment.annualRefresh ?? false);
            setSelectedContentPack(assignment.contentPackId || '');

            const scope = assignment.scope || assignment.groupingType || '';
            const scopeId = assignment.scopeId;

            // Trigger grouping query to resolve scopeId to a name
            if (scope && scope !== 'Tenancy') {
                switch (scope) {
                    case 'Group': break; // resolved from props
                    case 'Location': triggerLocations({ tenancyId }); break;
                    case 'Department': triggerDepartments({ tenancyId }); break;
                    case 'City': triggerCities({ tenancyId }); break;
                    case 'Country': triggerCountries({ tenancyId }); break;
                    case 'CompanyName': triggerCompanyNames({ tenancyId }); break;
                    case 'State': triggerStates({ tenancyId }); break;
                    default: break;
                }

                // For groups, resolve immediately from props
                if (scope === 'Group' && scopeId) {
                    const group = groups.find(g => String(g.userGroupId) === String(scopeId));
                    setRecipientName(group?.name || scopeId);
                } else {
                    setRecipientName(scopeId || '');
                }
            } else {
                setRecipientName('');
            }
        }
    }, [assignment]);

    // Resolve scopeId to name once grouping data loads
    useEffect(() => {
        if (!assignment) return;
        const scope = assignment.scope || assignment.groupingType || '';
        const scopeId = assignment.scopeId;
        if (!scopeId || scope === 'Tenancy' || scope === 'Group') return;

        const lookupData = {
            'Location': locationsData,
            'Department': departmentsData,
            'City': citiesData,
            'Country': countriesData,
            'CompanyName': companyNamesData,
            'State': statesData,
        }[scope];

        if (lookupData) {
            const found = lookupData.find(item => String(item.id) === String(scopeId));
            if (found) setRecipientName(found.name);
        }
    }, [assignment, locationsData, departmentsData, citiesData, countriesData, companyNamesData, statesData]);

    const hasChanged = useMemo(() => {
        if (!assignment) return false;
        const origRefresh = assignment.refreshEnabled ?? assignment.annualRefresh ?? false;
        const origContentPack = assignment.contentPackId || '';
        return starterPack !== (assignment.starterPack ?? true)
            || annualRefresh !== origRefresh
            || selectedContentPack !== origContentPack;
    }, [assignment, starterPack, annualRefresh, selectedContentPack]);

    const handleConfirm = () => {
        if (assignment) {
            onConfirm({
                id: assignment.id,
                starterPack,
                annualRefresh,
                contentPackId: selectedContentPack
            });
        }
    };

    const contentPackName = contentPacks?.find(p => p.internalId === assignment?.contentPackId)?.contentPackName
        || assignment?.contentPackName || '';

    const scope = assignment?.scope || assignment?.groupingType || '';

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="edit-assignment-modal"
        >
            <Box sx={modalStyle}>
                <Typography variant="h6" sx={{ mb: 3 }}>Edit Assignment</Typography>

                {/* Read-only assignment info */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Content pack</InputLabel>
                        <Select
                            value={selectedContentPack}
                            onChange={(e) => setSelectedContentPack(e.target.value)}
                            label="Content pack"
                        >
                            {contentPacks.map((pack) => (
                                <MenuItem key={pack.internalId} value={pack.internalId}>
                                    {pack.contentPackName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" disabled>
                        <InputLabel>Scope</InputLabel>
                        <Select
                            value={scope}
                            label="Scope"
                        >
                            <MenuItem value={scope}>
                                {scope}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {scope && scope !== 'Tenancy' && recipientName && (
                        <MainCard
                            content={false}
                            boxShadow
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                overflow: 'hidden'
                            }}
                        >
                            <List sx={{ p: 0 }}>
                                <ListItem>
                                    <ListItemText
                                        primary={recipientName}
                                        secondary="Assigned recipient"
                                    />
                                </ListItem>
                            </List>
                        </MainCard>
                    )}
                </Box>

                {/* Editable: Starter Pack & Annual Refresh toggles */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Starter Pack</Typography>
                        <Switch
                            checked={starterPack}
                            onChange={(e) => setStarterPack(e.target.checked)}
                            color="primary"
                        />
                        <Typography variant="body2">Annual Refresh</Typography>
                        <Switch
                            checked={annualRefresh}
                            onChange={(e) => setAnnualRefresh(e.target.checked)}
                            color="primary"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={!hasChanged || isLoading}
                    >
                        {isLoading ? <CircularProgress size={20} color="inherit" /> : "Save"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
